import { Slider, Tooltip } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { useFormBuilderStore } from '~/src/dash/components/forms/formBuilder/contexts';
import { FormBuilderNumberInput } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/common/FormBuilderNumberInput';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { Col, Cols, Row, Rows, spacing, Text } from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';

export interface IStepSliderProps {
  key: string;
  minValueKey: string;
  maxValueKey: string;
  defaultMin?: number;
  defaultMax?: number;
  tooltip?: string;
  generateMarks?: (selectedValues: number[]) => {
    value: number;
    label: string;
  }[];
  note?: string;
  measurement?: string;
}

export const StepSlider = ({
  minValueKey,
  maxValueKey,
  defaultMin = 0,
  defaultMax = 300,
  generateMarks,
  tooltip,
  note,
  measurement,
}: IStepSliderProps) => {
  const { object, defaultObject } = useFormBuilderStore();

  const [min, setMin] = useState(object[minValueKey]);
  const [max, setMax] = useState(object[maxValueKey]);

  useEffect(() => {
    return () => {
      object[minValueKey] = defaultObject[minValueKey];
      object[maxValueKey] = defaultObject[maxValueKey];
    };
  }, []);

  const handleChangeMin = (newMin: number) => {
    let validatedMin = newMin;
    switch (true) {
      case newMin < defaultMin:
        validatedMin = defaultMin;
      case newMin > max:
        validatedMin = max;
    }
    setMin(validatedMin);
    object[minValueKey] = validatedMin;
  };

  const handleChangeMax = (newMax: number) => {
    let validatedMax = newMax;
    switch (true) {
      case newMax > defaultMax:
        validatedMax = defaultMax;
      case newMax < min:
        validatedMax = min;
    }
    setMax(validatedMax);
    object[maxValueKey] = validatedMax;
  };

  const handleSlide = (newValues: number | number[]) => {
    if (typeof newValues === 'object' && newValues.length === 2) {
      // preventative logic
      let newMin = newValues[0];
      let newMax = newValues[1];
      if (newMin > max && newMax === max) newMin = max;
      if (newMax < min && newMin === min) newMax = min;

      object[minValueKey] = newMin;
      object[maxValueKey] = newMax;
      unstable_batchedUpdates(() => {
        setMin(newMin);
        setMax(newMax);
      });
    }
  };

  return (
    <Rows>
      <Row noPadding>
        <Cols gap={spacing.small} style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <Col displayFlex paddingTop={spacing.small}>
            {tooltip && (
              <Tooltip title={tooltip} placement='top' interactive>
                <div>
                  <IconV2 name='info-static' color={publicaColors.gray} />
                </div>
              </Tooltip>
            )}
          </Col>
          <Col flex>
            <Cols gap={spacing.base} style={{ alignItems: 'center', justifyContent: 'flex-end' }}>
              <Col>
                <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
                  <Col>
                    <Text small bold disableSelect muted>
                      {`Min${measurement ? ` (${measurement})` : ``}`}
                    </Text>
                  </Col>
                  <Col>
                    <FormBuilderNumberInput
                      value={min}
                      handleOnChange={handleChangeMin}
                      inputID='min'
                      key='min'
                      min={defaultMin}
                      max={max}
                    />
                  </Col>
                </Cols>
              </Col>
              <Col>
                <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
                  <Col>
                    <Text small bold disableSelect muted>
                      {`Max${measurement ? ` (${measurement})` : ``}`}
                    </Text>
                  </Col>
                  <Col>
                    <FormBuilderNumberInput
                      value={max}
                      handleOnChange={handleChangeMax}
                      inputID='max'
                      key='max'
                      min={min}
                      max={defaultMax}
                    />
                  </Col>
                </Cols>
              </Col>
            </Cols>
          </Col>
          {note && (
            <Col style={{ maxWidth: '120px' }}>
              <Text extraSmall muted italic style={{ lineHeight: '12px', textAlign: 'right' }}>
                {note}
              </Text>
            </Col>
          )}
        </Cols>
      </Row>
      <Row style={{ padding: '4px 16px 4px 8px' }}>
        <Slider
          value={[...[min, max]]}
          min={defaultMin}
          max={defaultMax}
          {...(generateMarks && { marks: generateMarks([min, max]) })}
          step={null}
          valueLabelDisplay='auto'
          valueLabelFormat={(num: number) =>
            num === defaultMax ? (
              <Text large color='inherit'>
                ∞
              </Text>
            ) : (
              num
            )
          }
          style={{
            width: '100%',
            color: publicaColors.teal,
            textAlign: 'center',
          }}
          onChange={(_e, newValues) => handleSlide(newValues)}
          key={minValueKey}
        />
      </Row>
    </Rows>
  );
};
